import { calcularFinanzasPropiedad } from "./finanzasPropiedad";
import { calcularProrrateoParaPropiedad } from "./prorrateoCostos";
import { safeMoneyNumber } from "./contextoFinanciero";
import {
  deudaPerteneceAPropiedad,
  distribuirDeudasParaPropiedad,
  esPropiedadVendida,
  participaInventarioKye,
} from "./compatFinanciera";

const SIN_FRACC_ID = "sin_fraccionamiento";

const getFraccionamientoKey = (propiedad = {}) =>
  propiedad.fraccionamientoId || propiedad.fraccNombre || propiedad.fraccionamientoNombre || SIN_FRACC_ID;

const getFraccionamientoNombre = (propiedad = {}) =>
  propiedad.fraccNombre ||
  propiedad.fraccionamientoNombre ||
  propiedad.fraccionamientoId ||
  "Sin fraccionamiento";

const isDisponible = (propiedad = {}, deudas = []) => {
  if (esPropiedadVendida(propiedad)) return false;
  const estado = String(propiedad.estado || "").trim().toLowerCase();
  return propiedad.disponible === true || estado === "disponible" || !estado;
};

const emptyFraccionamiento = (id, nombre) => ({
  id,
  nombre,
  lotesTotales: 0,
  lotesVendidos: 0,
  lotesDisponibles: 0,
  ingresoVendido: 0,
  ingresoProyectado: 0,
  ingresoCobrado: 0,
  saldoPorCobrar: 0,
  costosDirectos: 0,
  costosProrrateados: 0,
  costoTotalCapitalizable: 0,
  utilidadProyectada: 0,
  utilidadRealizada: 0,
  margenPromedio: null,
});

export const construirResumenFraccionamientos = ({
  propiedades = [],
  deudas = [],
  gastos = [],
  catalogos = {},
} = {}) => {
  const rowsMap = new Map();
  const propiedadesActivas = propiedades.filter((p) => p.activo !== false);

  propiedadesActivas.forEach((propiedad) => {
    const fraccionamientoId = getFraccionamientoKey(propiedad);
    const fraccionamientoNombre = getFraccionamientoNombre(propiedad);
    const deudasPropiedadBase = deudas.filter((deuda) =>
      deudaPerteneceAPropiedad(deuda, propiedad.id)
    );
    const deudasPropiedad = distribuirDeudasParaPropiedad(
      deudasPropiedadBase,
      propiedad,
      propiedadesActivas
    );
    const gastosDirectos = gastos.filter((gasto) => gasto.propiedadId === propiedad.id);
    const prorrateo = calcularProrrateoParaPropiedad({
      propiedad,
      gastos,
      propiedades,
      catalogos,
    });
    const finanzas = calcularFinanzasPropiedad({
      propiedad,
      deudas: deudasPropiedad,
      gastosDirectos,
      costosProrrateados: prorrateo.totalProrrateado,
    });

    if (!rowsMap.has(fraccionamientoId)) {
      rowsMap.set(
        fraccionamientoId,
        emptyFraccionamiento(fraccionamientoId, fraccionamientoNombre)
      );
    }

    const row = rowsMap.get(fraccionamientoId);
    const participaInventario = participaInventarioKye(propiedad);
    row.lotesTotales += participaInventario ? 1 : 0;
    row.lotesVendidos += participaInventario && esPropiedadVendida(propiedad) ? 1 : 0;
    row.lotesDisponibles += participaInventario && isDisponible(propiedad, deudasPropiedad) ? 1 : 0;
    row.ingresoVendido += finanzas.ingresoVendido;
    row.ingresoProyectado += finanzas.ingresoProyectado;
    row.ingresoCobrado += finanzas.totalCobrado;
    row.saldoPorCobrar += finanzas.saldoPendiente;
    row.costosDirectos += finanzas.costosDirectosCapitalizables;
    row.costosProrrateados += finanzas.costosProrrateados;
    row.costoTotalCapitalizable += finanzas.costoTotalAsignado;
    row.utilidadProyectada += finanzas.utilidadProyectadaConProrrateo;
    row.utilidadRealizada += finanzas.utilidadRealizadaConProrrateo;
  });

  const rows = Array.from(rowsMap.values())
    .map((row) => ({
      ...row,
      margenPromedio:
        row.ingresoProyectado > 0 ? row.utilidadProyectada / row.ingresoProyectado : null,
    }))
    .sort((a, b) => safeMoneyNumber(b.ingresoVendido) - safeMoneyNumber(a.ingresoVendido));

  const resumen = rows.reduce(
    (acc, row) => {
      acc.lotesTotales += row.lotesTotales;
      acc.lotesVendidos += row.lotesVendidos;
      acc.lotesDisponibles += row.lotesDisponibles;
      acc.ingresoVendido += row.ingresoVendido;
      acc.ingresoProyectado += row.ingresoProyectado;
      acc.ingresoCobrado += row.ingresoCobrado;
      acc.saldoPorCobrar += row.saldoPorCobrar;
      acc.costosDirectos += row.costosDirectos;
      acc.costosProrrateados += row.costosProrrateados;
      acc.costoTotalCapitalizable += row.costoTotalCapitalizable;
      acc.utilidadProyectada += row.utilidadProyectada;
      acc.utilidadRealizada += row.utilidadRealizada;
      return acc;
    },
    {
      lotesTotales: 0,
      lotesVendidos: 0,
      lotesDisponibles: 0,
      ingresoVendido: 0,
      ingresoProyectado: 0,
      ingresoCobrado: 0,
      saldoPorCobrar: 0,
      costosDirectos: 0,
      costosProrrateados: 0,
      costoTotalCapitalizable: 0,
      utilidadProyectada: 0,
      utilidadRealizada: 0,
      margenPromedio: null,
    }
  );

  resumen.margenPromedio =
    resumen.ingresoProyectado > 0
      ? resumen.utilidadProyectada / resumen.ingresoProyectado
      : null;

  return { resumen, rows };
};
